import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardTitle } from '@auctisync/ui';
import { Layout } from '../../components/Layout';
import { PageHead } from '../../components/PageHead';
import { fetchHelpArticles, fetchHelpCategories, type HelpCategory } from '@auctisync/api-client';
import { useTenant } from '../../contexts/TenantContext';
import { supabase } from '../../lib/supabase';

interface CategoryWithCount extends HelpCategory {
  article_count: number;
}

export function Home() {
  const tenant = useTenant();
  const [categories, setCategories] = useState<CategoryWithCount[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      if (!supabase) {
        setError('Help Center backend is warming up. Try again in a moment.');
        return;
      }
      // Platform mode: only tenant_id IS NULL.
      // White-label mode: tenant_id IS NULL OR tenant_id = <this tenant>.
      // Both scopings live in @auctisync/api-client now — the `.or(...)` string
      // is exactly the kind of thing that gets copied with a typo and then
      // returns every house's private articles to everybody.
      const tenantId = tenant?.id ?? null;
      const cats = await fetchHelpCategories(supabase, tenantId);
      if (cats.error) {
        setError('Could not load help categories.');
        return;
      }
      // No audience filter: the public site lists staff docs on purpose, so a
      // prospective auction house can read them before signing up.
      const arts = await fetchHelpArticles(supabase, { tenantId });
      if (arts.error) {
        setError('Could not load article counts.');
        return;
      }
      const counts = new Map<string, number>();
      for (const row of arts.articles) {
        if (row.category_id) {
          counts.set(row.category_id, (counts.get(row.category_id) ?? 0) + 1);
        }
      }
      setCategories(cats.categories.map((c) => ({ ...c, article_count: counts.get(c.id) ?? 0 })));
    }
    void load();
  }, [tenant]);

  // Hide categories with no anon-visible published articles. Empty cards for
  // future verticals (Facilities / Liquidators / Specialty) read as unfinished
  // and advertise products that aren't live yet (no-ghost-features); a staff-
  // only category like "For Auction Houses" otherwise shows "0 articles" to
  // anonymous visitors even though it's full. Filter on the RLS-filtered count
  // the page already computes.
  const visibleCategories = categories?.filter((cat) => cat.article_count > 0) ?? null;

  return (
    <Layout>
      <PageHead
        title="AuctiSync Help Center"
        description="Help articles for every AuctiSync audience — auctioneers, facility operators, liquidators, specialty dealers, bidders, consignors."
        canonicalPath="/"
      />
      <section className="mx-auto max-w-5xl px-6 pt-16 pb-12">
        <h1
          className="text-4xl font-bold tracking-tight md:text-5xl"
          style={{ color: 'var(--text)' }}
        >
          AuctiSync Help Center
        </h1>
        <p className="mt-4 max-w-2xl text-lg" style={{ color: 'var(--text-muted)' }}>
          Articles for every audience on the platform — browse by category below, or ask the AI
          assistant for an answer grounded in the docs.
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-16">
        {error ? (
          <p style={{ color: 'var(--semantic-error)' }}>{error}</p>
        ) : !visibleCategories ? (
          <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
        ) : visibleCategories.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>No articles published yet.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {visibleCategories.map((cat) => (
              <a key={cat.id} href={`/c/${cat.slug}`} className="group">
                <Card className="h-full transition-transform hover:-translate-y-0.5">
                  <CardContent className="flex h-full flex-col gap-2 p-6">
                    <CardTitle className="text-lg">{cat.title}</CardTitle>
                    <CardDescription>
                      {cat.description ?? 'Articles in this category.'}
                    </CardDescription>
                    <span
                      className="mt-2 text-xs tracking-wide uppercase"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      {cat.article_count} {cat.article_count === 1 ? 'article' : 'articles'}
                    </span>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
